import { useEffect, useRef, useState } from "react";
import { MapPin, Plus, Loader2, Search, BadgeCheck, PencilLine } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

const fullAddress = (place) => {
  if (!place) return "";
  if (place.address) return place.address;
  return [place.comune, place.provincia, place.cap].filter(Boolean).join(", ");
};

// Casella intelligente di ricerca luogo (Nominatim/OpenStreetMap, gratuito).
// value: testo; onChange(text); onSelectPlace(place|null); selected: luogo già scelto (mostra scheda nome+indirizzo).
export function PlaceSearch({ value, onChange, onSelectPlace, selected, placeholder = "Cerca deposito, azienda o indirizzo", testId }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const lastQuery = useRef("");

  useEffect(() => {
    if (selected) return;
    const q = (value || "").trim();
    if (q.length < 2 || q === lastQuery.current) {
      if (q.length < 2) setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const found = await apiFetch(`/geocode/search?q=${encodeURIComponent(q)}`);
        setResults(Array.isArray(found) ? found : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [value, selected]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (r) => {
    lastQuery.current = r.name || r.comune || "";
    onChange(r.name || r.comune || value);
    onSelectPlace({ ...r, manual: false });
    setOpen(false);
  };

  const manual = () => {
    const text = (value || "").trim();
    if (!text) return;
    lastQuery.current = text;
    onSelectPlace({ name: text, address: "", comune: "", provincia: "", cap: "", lat: null, lon: null, manual: true });
    setOpen(false);
  };

  const change = () => {
    lastQuery.current = "";
    onSelectPlace(null);
    setResults([]);
    setOpen(true);
  };

  const taRef = useRef(null);
  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(50, el.scrollHeight)}px`;
  };
  useEffect(() => {
    if (!selected) autoGrow(taRef.current);
  }, [value, selected]);

  if (selected) {
    const addr = fullAddress(selected);
    const verified = !selected.manual && (selected.lat != null || !!selected.address);
    return (
      <div data-testid={testId ? `${testId}-selected` : undefined} className="rounded-xl border border-border bg-card p-3">
        <div className="flex items-start gap-2">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="whitespace-pre-wrap break-words text-[15px] font-extrabold text-foreground">{selected.name || value}</p>
            {addr ? <p className="mt-0.5 whitespace-pre-wrap break-words text-[13px] text-muted-foreground">{addr}</p> : null}
            {verified ? (
              <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-extrabold text-emerald-700">
                <BadgeCheck className="h-3.5 w-3.5" /> Indirizzo verificato
              </span>
            ) : (
              <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-extrabold text-amber-700">
                <PencilLine className="h-3.5 w-3.5" /> Inserito manualmente · Posizione da verificare
              </span>
            )}
          </div>
          <button type="button" data-testid={testId ? `${testId}-change` : undefined} onClick={change} className="shrink-0 rounded-full border border-border px-3 py-1.5 text-xs font-extrabold text-foreground hover:bg-secondary">
            Cambia
          </button>
        </div>
      </div>
    );
  }

  const inputCls = "min-h-[50px] w-full resize-none overflow-hidden whitespace-pre-wrap break-words rounded-xl border border-border bg-card py-3 pl-9 pr-3 text-[15px] leading-snug outline-none focus:border-primary";

  return (
    <div ref={boxRef} className="relative">
      <Search className="pointer-events-none absolute left-3 top-[15px] h-4 w-4 text-muted-foreground" />
      <textarea
        ref={taRef}
        data-testid={testId}
        value={value}
        rows={1}
        onChange={(e) => { onChange(e.target.value); onSelectPlace(null); setOpen(true); autoGrow(e.target); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className={inputCls}
        autoComplete="off"
      />
      {open && (value || "").trim().length >= 2 ? (
        <div data-testid={testId ? `${testId}-dropdown` : undefined} className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-xl border border-border bg-card shadow-lg">
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Ricerca in corso…</div>
          ) : null}
          {results.map((r, i) => (
            <button
              key={`${r.lat}-${r.lon}-${i}`}
              type="button"
              data-testid={testId ? `${testId}-option-${i}` : undefined}
              onClick={() => pick(r)}
              className="flex w-full items-start gap-2 border-b border-border/60 px-3 py-2.5 text-left last:border-0 hover:bg-secondary"
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span className="min-w-0">
                <span className="block truncate text-[14px] font-bold text-foreground">{r.name || r.comune || "Luogo"}</span>
                {r.address ? <span className="block truncate text-[12px] text-muted-foreground">{r.address}</span> : null}
                <span className="block truncate text-[11px] font-semibold text-muted-foreground">
                  {[r.comune, r.provincia, r.cap].filter(Boolean).join(" · ") || "—"}
                </span>
              </span>
            </button>
          ))}
          {!loading && results.length === 0 ? (
            <p className="px-3 py-2.5 text-[13px] text-muted-foreground">Nessun luogo trovato.</p>
          ) : null}
          <button
            type="button"
            data-testid={testId ? `${testId}-manual` : undefined}
            onClick={manual}
            className={cn("flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] font-extrabold text-primary hover:bg-secondary", results.length ? "border-t border-border" : "")}
          >
            <Plus className="h-4 w-4" /> Inserisci questo luogo manualmente
          </button>
        </div>
      ) : null}
    </div>
  );
}
